import React, { Fragment, useRef, useContext } from "react";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import {
    SearchIcon,
    CogIcon,
    SupportIcon,
    TrashIcon,
    CheckIcon,
} from "@heroicons/react/outline";
import { Link, useHistory, useLocation } from "react-router-dom";
import { modal } from "components/HeadlessModal";
import { AssignmentContext } from "context/Assignment";
import { useAuth } from "hooks/Auth";
import { toast } from "react-hot-toast";
import { apiDel, apiPut, messageFromException } from "helpers/Request";
import { useContextualHelp } from "hooks/Help";
import SecondaryButton from "components/Buttons/SecondaryButton";
import DangerButton from "components/Buttons/DangerButton";

export default function AssignmentListToolbar(props) {
    const inputRef = useRef();
    const history = useHistory();
    const location = useLocation();
    const auth = useAuth();
    const contextualHelp = useContextualHelp();

    const { selectedItems, load } = useContext(AssignmentContext);

    const updateParams = (key, val = null) => {
        const params = new URLSearchParams(location.search);

        if (typeof key === "object") {
            Object.entries(key).forEach(([k, v]) => {
                params.set(k, v);
            });
        } else {
            if (params.has(key) && val === null) {
                params.delete(key);
            } else {
                params.set(key, val);
            }
        }

        history.replace(location.pathname + "?" + params.toString());
    };

    const onRequestDelete = () => {
        modal.warning({
            title: "Delete Assignments",
            children: (
                <Fragment>
                    <div>
                        Are you sure you want to delete these assignments?
                    </div>
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={handleDelete}>Delete</DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const handleDelete = async () => {
        const id = toast.loading("Deleting assignments...");

        const promises = selectedItems.map((uuid) =>
            apiDel(`/v4/assignment/${uuid}`, auth.publication)
        );
        return Promise.all(promises)
            .then(() => {
                toast.success("Assignments deleted", { id });
                modal.dismiss();
                load();
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Error Deleting Assignments",
                    children: messageFromException(err),
                });
            });
    };

    const onRequestClose = async () => {
        const id = toast.loading("Marking assignments as complete...");

        const promises = selectedItems.map((uuid) =>
            apiPut(`/v4/assignment/${uuid}`, auth.publication, {
                status: 0,
                acquire_lock: true,
            })
        );
        return Promise.all(promises)
            .then(() => {
                toast.success("Assignments completed", { id });
                modal.dismiss();
                load();
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Error Updating Assignments",
                    children: messageFromException(err),
                });
            });
    };

    const onKeyDown = (e) => {
        if (e.key !== "Enter") {
            return;
        }
        onSearchClick();
    };
    const onSearchClick = () => {
        const val = inputRef.current.value;
        if (!val || !val.length) {
            updateParams("keywords", null);
        } else {
            updateParams("keywords", val);
        }
    };

    return (
        <Toolbar>
            <ToolbarGroup>
                <input
                    type="text"
                    ref={inputRef}
                    className="focus:outline-none relative mr-2 flex w-3/4 items-center rounded-md border border-gray-300 bg-white py-0 px-2 text-xs placeholder-gray-500 focus:border-green-500 focus:text-gray-900 focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 sm:w-auto"
                    placeholder="Search Assignments"
                    onKeyDown={onKeyDown}
                />
                <ToolbarButton icon={<SearchIcon />} onClick={onSearchClick} />
            </ToolbarGroup>
            <ToolbarGroup>
                <ToolbarButton
                    icon={<CogIcon />}
                    as={Link}
                    to="/app/planner/issue"
                >
                    <span className="hidden sm:inline">Manage Issues</span>
                </ToolbarButton>
                <ToolbarButton
                    icon={<CheckIcon />}
                    onClick={onRequestClose}
                    disabled={!selectedItems.length}
                >
                    Complete
                </ToolbarButton>
                <ToolbarButton
                    icon={<TrashIcon />}
                    onClick={onRequestDelete}
                    disabled={!selectedItems.length}
                >
                    Delete
                </ToolbarButton>
            </ToolbarGroup>
            <ToolbarGroup className="ml-auto">
                <ToolbarButton
                    icon={<SupportIcon />}
                    className="text-green-600"
                    onClick={() =>
                        contextualHelp.showContextualHelp("/planner/index.html")
                    }
                >
                    Help
                </ToolbarButton>
            </ToolbarGroup>
        </Toolbar>
    );
}
